import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ArrowLeft } from "lucide-react";

// Step-two of login when 2FA is enabled. Renders a code entry that verifies the
// short-lived challenge and, on success, establishes the session. The "Trust this
// device for 30 days" choice is made ONCE on the password step and carried in `remember`.
export function TwoFactorChallenge({ challenge, onSuccess, onCancel, accent = "#FFFFFF", accentText = "#000000", remember = false }) {
  const { verify2fa } = useAuth();
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) return setError("Enter your code");
    setLoading(true);
    setError("");
    const payload = useRecovery ? { recovery_code: value.trim() } : { code: value.trim() };
    payload.trust_device = remember;
    payload.remember = remember;
    const res = await verify2fa(challenge, payload);
    setLoading(false);
    if (res.ok) onSuccess?.();
    else setError(res.error || "Verification failed");
  };

  return (
    <div data-testid="twofa-challenge">
      <div className="mb-4 flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/[0.06]"><ShieldCheck className="h-4.5 w-4.5 text-foreground" /></div>
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Two-factor authentication</h1>
          <p className="text-sm text-muted-foreground">{useRecovery ? "Enter one of your recovery codes." : "Enter the 6-digit code from your authenticator app."}</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{useRecovery ? "Recovery code" : "Authentication code"}</Label>
          <Input
            data-testid="twofa-code-input"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={useRecovery ? "XXXXX-XXXXX" : "123456"}
            inputMode={useRecovery ? "text" : "numeric"}
            className="border-white/[0.1] bg-[#0A0A0A] tracking-widest focus-visible:ring-white/20"
          />
          {error && <p className="text-xs text-[#EF4444]" data-testid="twofa-error">{error}</p>}
        </div>
        <Button type="submit" disabled={loading} data-testid="twofa-submit" className="w-full hover:opacity-90" style={{ background: accent, color: accentText }}>
          {loading ? "Verifying…" : "Verify"}
        </Button>
      </form>
      <div className="mt-4 flex items-center justify-between text-xs">
        <button type="button" onClick={() => { setUseRecovery((v) => !v); setValue(""); setError(""); }} data-testid="twofa-toggle-recovery" className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
          {useRecovery ? "Use authenticator code" : "Use a recovery code instead"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} data-testid="twofa-cancel" className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        )}
      </div>
    </div>
  );
}
